import { ExternalLink } from "lucide-react";
import { Button } from "@/shared/components/shadcn/Button";
import { ErrorState } from "@/shared/components/ui/ErrorState";
import { LoadingSpinner } from "@/shared/components/ui/LoadingSpinner";
import { useSubscription } from "../hooks/subscriptionQueries";
import { getManageSubscriptionUrl } from "../utils/subscriptionUrls";
import { isFreePlan } from "../utils/subscriptionFormat";
import { CurrentPlanCard } from "./CurrentPlanCard";
import { SubscriptionHostRefresh } from "./SubscriptionHostRefresh";

interface PlanSettingsProps {
  businessId: string;
  userId: string;
}

/** In-app plan status. Plans are bought and managed on the web, never inside the app. */
export function PlanSettings({ businessId, userId }: PlanSettingsProps) {
  const query = useSubscription(businessId, userId);
  const subscription = query.data;

  return (
    <div className="space-y-6">
      <SubscriptionHostRefresh businessId={businessId} />
      {query.isPending ? (
        <LoadingSpinner message="Loading your plan..." />
      ) : query.error ? (
        <ErrorState
          title="Couldn't load your plan"
          message="Plan details are unavailable. Please try again."
          onRetry={() => void query.refetch()}
        />
      ) : subscription && (
        <CurrentPlanCard
          subscription={subscription}
          action={(
            <Button asChild variant={isFreePlan(subscription) ? "outline" : "default"}>
              <a href={getManageSubscriptionUrl(businessId)} target="_blank" rel="noopener noreferrer">
                Manage subscription
                <ExternalLink aria-hidden="true" />
              </a>
            </Button>
          )}
        />
      )}
      <p className="text-xs leading-relaxed text-muted-foreground">
        Your subscription is managed on the Froppio website. Come back here to see your updated plan. Usage limits reset monthly.
      </p>
    </div>
  );
}
